import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import { IPartners } from '../../../interfaces/partners/IPartners';
import { IToast } from '../../../interfaces/components/IToast';
import {
    tryToDisablePartner,
    tryToEnablePartner,
    tryToFetchPartners
} from '../../../store/brokers/admin/partners/partnersSlice';
import PartnerDetailsModal from './modals/PartnerDetailsModal';
import InvitePartnerModal from './modals/InvitePartnerModal';
import DeletePartnerModal from './modals/DeletePartnerModal';
import PartnersTable from './tables/PartnersTable';
import Button from '../../../shared/button';
import Input from '../../../shared/input';
import Toast from '../../../shared/toast';


const Users = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<any[]>([]);
    const [search, setSearch] = useState<string>()
    const [showToast, setShowToast] = useState<IToast | null>()
    const [showInviteModal, setShowInviteModal] = useState<boolean>(false)
    const [showDetailsModal, setShowDetailsModal] = useState<{ show: boolean, data: any }>();
    const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean, id: string }>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const paginationData = useAppSelector((state) => state.partners.partners?.data?.page);
    const rowsLoading = useAppSelector((state) => state.partners.partnersAreLoading);
    const state = useAppSelector((state) => state.partners);

    const onClickShowInviteModal = () => {
        setShowInviteModal(true)
    }

    useEffect(() => {
        if (state.partners) {
            const rows =
                state.partners?.data?.elements && Array.isArray(state.partners?.data.elements)
                    ? state.partners?.data.elements.map((r: IPartners) => ({ ...r, id: r._id }))
                    : [];
            setRows(rows);
        }
    }, [state.partners]);

    const onGettingFilters = (type?: string) => {
        let settingsFilters: any
        if (!type || type !== 'download') {
            settingsFilters = {
                ...paginationState
            }
        }
        if (search) {
            settingsFilters.data = {
                ...settingsFilters.data,
                search: search
            }
        }
        return settingsFilters
    }

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const getPartnersData = async () => {
        const settingsFilters = onGettingFilters()
        await dispatch(tryToFetchPartners(settingsFilters));
    }

    useEffect(() => {
        getPartnersData()
    }, [paginationState])

    const onSeePartnerDetails = (data: any) => {
        setShowDetailsModal({
            show: true,
            data: data
        })
        document.body.style.overflow = 'hidden';
    }

    const onDeletePartnerRequest = (id: string) => {
        setShowDeleteModal({
            show: true,
            id: id
        })
    }

    const onCloseModals = () => {
        setShowDeleteModal(undefined)
        setShowDetailsModal(undefined)
        setShowInviteModal(false)
        document.body.style.overflow = 'auto';
    }

    const onChangeSearch = (value: string | null | undefined, type: string) => {
        setSearch(value || undefined)
        onResetPaginationState()
    }

    const onEnableDisablePartner = async (id: string, currentStatus: boolean) => {
        try {
            if (currentStatus) {
                await dispatch(tryToDisablePartner(id)).unwrap();
                setShowToast({ type: 'success', message: `Partner disabled successfully` });
            } else {
                await dispatch(tryToEnablePartner(id)).unwrap();
                setShowToast({ type: 'success', message: `Partner enabled successfully` });
            }
        } catch (error) {
            setShowToast({ type: 'error', message: `${error}` });
        }
    }

    const onClickShowInvitedPartner = () => {
        navigate('/users/invited')
    }

    return (
        <div>
            <div className='mb-6'>
                <p className='page-title'>Partners</p>
            </div>
            <div className='mt-7 mb-7'>
                <div className='flex flex-row justify-end mb-10'>
                    <Button
                        label={'List of Invited Partners'}
                        className='btn-primary-rounded-less mr-3'
                        onClickButton={onClickShowInvitedPartner}
                    />
                    <Button
                        label={'Invite New Partner'}
                        className='btn-primary-rounded-less'
                        onClickButton={onClickShowInviteModal}
                    />
                </div>
                <div className={`flex flex-row justify-between items-center bg-call-default mt-3 p-1 !rounded mb-2`}>
                    <Input
                        placeholder={'Search for Partner'}
                        dataQa={'search'}
                        containerStyle='mb-0 pb-0 relative min-w-[60%]'
                        inputStyle={`mb-0 border-0 focus:outline-none`}
                        icon={'/assets/shared/search.svg'}
                        showValue={true}
                        inputValue={search}
                        inputUniqueName={'search'}
                        onChangeInput={onChangeSearch}
                        hideInputSpace={true}
                    />
                </div>
                <PartnersTable
                    rows={rows}
                    rowsLoading={rowsLoading}
                    paginationData={paginationData}
                    onSeePartnerDetails={onSeePartnerDetails}
                    setPaginationState={setPaginationState}
                    onDeletePartnerRequest={onDeletePartnerRequest}
                    onEnableDisablePartner={onEnableDisablePartner}
                />
            </div>
            {showInviteModal &&
                <InvitePartnerModal
                    openModal={showInviteModal}
                    handleCloseModal={onCloseModals}
                />
            }
            {showDetailsModal?.show &&
                <PartnerDetailsModal
                    openModal={showDetailsModal?.show}
                    data={showDetailsModal?.data}
                    handleCloseModal={onCloseModals}
                />
            }
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
            {showDeleteModal?.show &&
                <DeletePartnerModal
                    openModal={showDeleteModal?.show}
                    partnerId={showDeleteModal?.id || ''}
                    handleCloseModal={() => setShowDeleteModal(undefined)}
                />
            }
        </div>
    )
}

export default Users;